
import { useState, useEffect } from "react";
import { Trip } from "@/types/trip";
import { identifyRealDestination } from "@/utils/tripUtils";
import { getCityFromAirportCode } from "@/utils/airportMapping";
import Hey from "@/lib/hey-utils";

export function useProcessedTrips(futureTrips: Trip[], pastTrips: Trip[]) {
  const [processedFutureTrips, setProcessedFutureTrips] = useState<Trip[]>([]);
  const [processedPastTrips, setProcessedPastTrips] = useState<Trip[]>([]);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processTrips = async () => {
      try {
        console.debug("======== PROCESSING TRIPS IN HOOK ==========");
        console.debug("Future trips count:", futureTrips.length);
        if (futureTrips.length > 0) {
          console.debug("First future trip ID:", futureTrips[0].id);
          console.debug("First future trip destination:", futureTrips[0].destination);
        }

        // Process future trips
        const processedFuture = await Promise.all(futureTrips.map(async (trip) => {
          const processedTrip = { ...trip };
          
          // Only process if destination info is not already set
          if (!processedTrip.identifiedDestination || !processedTrip.destinationImage) {
            try {
              const mainDestinationCode = identifyRealDestination(processedTrip.elements || []);
              
              // Check if we have a valid airport code
              if (mainDestinationCode) {
                // Try to get city name from airport code
                const cityName = getCityFromAirportCode(mainDestinationCode);
                processedTrip.identifiedDestination = cityName !== "Unknown City" 
                  ? cityName 
                  : processedTrip.destination;
                
                try {
                  // Try to get airport info for the destination image
                  const airportInfo = await Hey.HeyUtils.getInstance().getAirportInfos(
                    mainDestinationCode, 
                    Hey.ImageFormat.CUSTOM2
                  );
                  
                  // Use the image from airport info or fall back to original image
                  processedTrip.destinationImage = 
                    (airportInfo?.imageUrl && airportInfo.imageUrl !== '') 
                      ? airportInfo.imageUrl 
                      : (processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png");
                } catch (imageError) {
                  console.error(`Error fetching image for ${mainDestinationCode}:`, imageError);
                  processedTrip.destinationImage = processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
                }
              } else {
                // No airport code found, use original values
                processedTrip.identifiedDestination = processedTrip.destination;
                processedTrip.destinationImage = processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
              }
            } catch (error) {
              console.error(`Error processing trip ${processedTrip.id}:`, error);
              processedTrip.identifiedDestination = processedTrip.destination;
              processedTrip.destinationImage = processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
            }
            
            console.debug(`Processed future trip ${processedTrip.id}:`, {
              originalDestination: trip.destination,
              identifiedDestination: processedTrip.identifiedDestination,
              originalImage: trip.image,
              destinationImage: processedTrip.destinationImage
            });
          }
          
          return processedTrip;
        }));
        
        // Process past trips (similar logic)
        const processedPast = await Promise.all(pastTrips.map(async (trip) => {
          const processedTrip = { ...trip };
          
          if (!processedTrip.identifiedDestination || !processedTrip.destinationImage) {
            try {
              const mainDestinationCode = identifyRealDestination(processedTrip.elements || []);
              
              if (mainDestinationCode) {
                const cityName = getCityFromAirportCode(mainDestinationCode);
                processedTrip.identifiedDestination = cityName !== "Unknown City" 
                  ? cityName 
                  : processedTrip.destination;
                
                try {
                  const airportInfo = await Hey.HeyUtils.getInstance().getAirportInfos(
                    mainDestinationCode, 
                    Hey.ImageFormat.CUSTOM2
                  );
                  
                  processedTrip.destinationImage = 
                    (airportInfo?.imageUrl && airportInfo.imageUrl !== '') 
                      ? airportInfo.imageUrl 
                      : (processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png");
                } catch (imageError) {
                  console.error(`Error fetching image for ${mainDestinationCode}:`, imageError);
                  processedTrip.destinationImage = processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
                }
              } else {
                processedTrip.identifiedDestination = processedTrip.destination;
                processedTrip.destinationImage = processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
              }
            } catch (error) {
              console.error(`Error processing trip ${processedTrip.id}:`, error);
              processedTrip.identifiedDestination = processedTrip.destination;
              processedTrip.destinationImage = processedTrip.image || "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
            }
            
            console.debug(`Processed past trip ${processedTrip.id}:`, {
              originalDestination: trip.destination,
              identifiedDestination: processedTrip.identifiedDestination,
              originalImage: trip.image,
              destinationImage: processedTrip.destinationImage
            });
          }
          
          return processedTrip;
        }));
        
        setProcessedFutureTrips(processedFuture);
        setProcessedPastTrips(processedPast);
      } catch (error) {
        console.error('Error in useProcessedTrips hook:', error);
        // Fallback to original trips if processing fails
        setProcessedFutureTrips(futureTrips);
        setProcessedPastTrips(pastTrips);
      } finally {
        setIsProcessing(false);
      }
    };
    
    // Reset processing state when trips change
    setIsProcessing(true);
    processTrips();
  }, [futureTrips, pastTrips]);

  return {
    processedFutureTrips,
    processedPastTrips,
    isProcessing
  };
}
